import { BriefcaseBusiness, Home, PenTool, Sofa, Sparkles, Stethoscope } from 'lucide-react';

export type Persona = {
  id: string;
  label: string;
  audience: string;
  pain: string;
  payoff: string;
  icon: typeof Sparkles;
  keywords: string[];
};

export const PERSONAS: Persona[] = [
  {
    id: 'home-services',
    label: 'Home Services',
    audience: 'Contractors, remodelers, HVAC, roofing',
    pain: 'Customers hesitate at high-ticket quotes and ask for time to think.',
    payoff: 'Offer monthly options instantly to reduce quote drop-off and close faster.',
    icon: Home,
    keywords: ['home improvement financing', 'contractor financing', 'monthly payment options'],
  },
  {
    id: 'creative-studios',
    label: 'Creative Studios',
    audience: 'Tattoo studios, med spas, beauty clinics',
    pain: 'Premium packages stall because customers cannot pay everything upfront.',
    payoff: 'Turn premium services into affordable monthly decisions at the point of sale.',
    icon: PenTool,
    keywords: ['tattoo financing', 'med spa financing', 'beauty financing options'],
  },
  {
    id: 'custom-retail',
    label: 'Vintage & Custom Retail',
    audience: 'Vintage furniture sellers, makers, design shops, high-end retail',
    pain: 'Shoppers love the piece but pause when the ticket size feels too heavy upfront.',
    payoff: 'Offer monthly options on the spot so smaller sellers can close without building a financing stack.',
    icon: Sofa,
    keywords: ['vintage furniture financing', 'furniture financing for small business', 'custom order financing', 'retail BNPL checkout'],
  },
  {
    id: 'professional-services',
    label: 'Professional Services',
    audience: 'Dental, wellness, coaching, education',
    pain: 'Clients want outcomes but need payment flexibility.',
    payoff: 'Present financing options clearly and ethically with Stripe-powered checkout.',
    icon: Stethoscope,
    keywords: ['service financing platform', 'client payment plans', 'pay over time checkout'],
  },
];

export const FUNNEL_STEPS = [
  {
    title: 'Create quote in seconds',
    detail: 'Enter amount once. Optional fields can stay empty.',
    metric: '10s',
  },
  {
    title: 'Share link or QR',
    detail: 'Use copy, SMS, QR, or native share from phone.',
    metric: '1 tap',
  },
  {
    title: 'Buyer chooses monthly options',
    detail: 'Klarna/Affirm/Afterpay shown at checkout when eligible.',
    metric: 'Live',
  },
  {
    title: 'Track conversion signals',
    detail: 'See viewed, checkout-started, paid, and follow-up impact.',
    metric: 'Real-time',
  },
];

export const PROOF_POINTS = [
  {
    title: 'Quote-to-link speed',
    value: 'Under 15s',
    description: 'Designed for live conversations and in-person selling moments.',
  },
  {
    title: 'Follow-up clarity',
    value: 'Behavior-led',
    description: 'Nudges appear only when a buyer signal indicates good timing.',
  },
  {
    title: 'Checkout trust',
    value: 'Stripe powered',
    description: 'Secure checkout with financing-capable methods where eligible.',
  },
  {
    title: 'Insights that teach',
    value: '30-day snapshot',
    description: 'Learn which channels and follow-up tones move paid conversion.',
  },
];

export const HOME_FAQS = [
  {
    q: 'How does LendLifted help close more sales?',
    a: 'LendLifted helps sellers present pay-over-time options at the exact moment a buyer is deciding, then follows buyer intent with actionable nudges and conversion insights.',
  },
  {
    q: 'Do I need inventory to use LendLifted?',
    a: 'No. LendLifted is built for services, custom jobs, and high-ticket projects where sellers need financing-enabled checkout links fast.',
  },
  {
    q: 'Which financing providers can buyers use?',
    a: 'LendLifted uses Stripe Checkout, which can present methods like Klarna, Afterpay/Clearpay, and Affirm when eligible for the buyer and transaction context.',
  },
  {
    q: 'Can I track what drives conversion?',
    a: 'Yes. Seller Studio shows quote views, checkout starts, paid conversion, follow-up usage, and channel/tone effectiveness so teams can improve close rates over time.',
  },
];

export const MARKET_KEYWORDS = [
  'seller financing software',
  'service business financing checkout',
  'BNPL for contractors',
  'high ticket payment link with financing',
  'close more sales with monthly payments',
];

export const PRIMARY_CTA_LABEL = 'Start Selling with Financing';
export const SECONDARY_CTA_LABEL = 'Explore How It Works';

export const VALUE_PILLARS = [
  {
    title: 'Conversion velocity',
    detail: 'Move from quote to checkout link in seconds while buyer intent is highest.',
    icon: BriefcaseBusiness,
  },
  {
    title: 'Decision confidence',
    detail: 'Buyers see practical pay-over-time options inside trusted Stripe checkout.',
    icon: Sparkles,
  },
  {
    title: 'Compounding learnings',
    detail: 'Every quote teaches your team what messaging and channels close faster.',
    icon: PenTool,
  },
];

export const SELLER_STORIES = [
  {
    id: 'vintage-furniture',
    title: 'Vintage furniture studio',
    profile: 'Independent seller, average ticket $1,400',
    before: 'Buyers saved items but delayed decisions for days while “figuring out payment.”',
    after: 'Seller shared QR in-store and converted same-day with pay-over-time checkout.',
    highlight: 'Faster close from showroom traffic',
  },
  {
    id: 'tattoo',
    title: 'Tattoo collective',
    profile: 'Custom sleeve work, average ticket $2,300',
    before: 'Large projects were broken into too many sessions due to budget hesitancy.',
    after: 'Studio shared financing links at consultation and booked larger packages confidently.',
    highlight: 'Higher package acceptance',
  },
  {
    id: 'contractor',
    title: 'Remodeling contractor',
    profile: 'Kitchen & bath projects, average ticket $9,800',
    before: 'Follow-up lag after quote delivery reduced close rates.',
    after: 'Behavior-led nudges helped reps follow up right when buyers reopened quotes.',
    highlight: 'More timely follow-up conversions',
  },
];

export const SEO_CLUSTERS = [
  {
    title: 'Seller Financing Fundamentals',
    summary: 'Understand how financing-enabled checkout increases confidence and reduces quote hesitation.',
    links: [
      { label: 'How seller financing works', href: '/features' },
      { label: 'Service business financing checkout', href: '/features' },
      { label: 'Monthly payment options for customers', href: '/features' },
    ],
  },
  {
    title: 'Pricing & Unit Economics',
    summary: 'Model platform costs, transaction flow, and expected conversion economics by ticket size.',
    links: [
      { label: 'LendLifted pricing overview', href: '/pricing' },
      { label: 'Platform fees vs provider fees', href: '/pricing' },
      { label: 'Cost planning for small sellers', href: '/pricing' },
      { label: 'Industry payment calculators', href: '/calculators' },
    ],
  },
  {
    title: 'Implementation for Small Teams',
    summary: 'Launch quickly even if you are not on Shopify or a commerce platform with built-in BNPL tooling.',
    links: [
      { label: 'Book implementation guidance', href: '/contact' },
      { label: 'Financing for vintage furniture shops', href: '/financing/vintage-furniture-financing' },
      { label: 'Vintage furniture financing in Austin', href: '/financing/vintage-furniture-financing/austin-tx' },
      { label: 'Contact sales engineering', href: '/contact' },
    ],
  },
];
